/**
 * The four parts a scenario is made of, each on its own.
 *
 * These used to live inside `LabScenarioDetail`. They were pulled out because the admin area
 * arranges them differently - it splits the steps by phase across a "Scenario" tab and a
 * "Validation" tab, and puts the criteria on a "Checks" tab of their own - and two copies of a
 * step table is how one of them ends up not offering `blast_radius`.
 *
 * Every panel owns its editor modal and its delete, so a caller composes them in any order
 * without threading modal state through. All of them take the scenario rather than an id: the
 * children arrive nested on it, and refetching per panel would show four different versions of
 * the same scenario on one screen.
 */

import React, { useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import {
  useAttachLabDevices,
  useDeleteLabRow,
  useDetachLabDevice,
  useLabDevices,
  useLabScenarioTriggers,
} from '../../hooks/lab-hooks';
import {
  LabPhase,
  LabScenario,
  LabDevice,
  LabScenarioStep,
  LabScenarioTrigger,
  LabSuccessCriterion,
  labEvaluatorLabel,
  labInsertModeLabel,
  labPhaseLabel,
  labStepInjectionBlockers,
  labTransportLabel,
  labTriggerActionLabel,
  labTriggerTypeLabel,
  LAB_PHASE_SEQUENCE,
  LAB_TABLES,
} from '../../support/labService';
import LabAttachPicker from './LabAttachPicker';
import LabCriterionEditorModal from './LabCriterionEditorModal';
import LabDeviceEditorModal from './LabDeviceEditorModal';
import LabInjectionModal from './LabInjectionModal';
import LabStepEditorModal from './LabStepEditorModal';
import LabTriggerEditorModal from './LabTriggerEditorModal';
import { LabCard, LabPanelState, LabStatusTag, labDuration, labErrorMessage } from './LabShared';

export type LabScenarioPanelProps = {
  scenario: LabScenario;
  /**
   * Authoring. Reading a scenario is open; changing what pods will be asked to do is not, and
   * neither is pushing a step at pods that are already running. The API refuses either way -
   * this only decides whether the buttons are there to click.
   */
  canEdit?: boolean;
};

/**
 * Delete with a confirmation, and the failure kept where the panel can show it.
 *
 * The confirmation says what else goes: a step's run records are cascaded, so deleting a step
 * after an event silently takes the evidence of who did it with it.
 */
const useRowRemoval = () => {
  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = async (
    mutation: { mutateAsync: (id: number) => Promise<any> },
    rowId: number,
    what: string,
  ) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete this ${what}? Run records that reference it go with it.`)) return;

    setRemoveError(null);

    try {
      await mutation.mutateAsync(rowId);
    } catch (exc) {
      setRemoveError(labErrorMessage(exc, `Could not delete the ${what}.`));
    }
  };

  const removeErrorNode = removeError ? (
    <p className="m-0 mb-3 text-sm text-amber-800">{removeError}</p>
  ) : null;

  return { remove, removeErrorNode };
};

const sortedSteps = (scenario: LabScenario): LabScenarioStep[] =>
  [...(scenario.steps || [])].sort(
    (a, b) =>
      LAB_PHASE_SEQUENCE.indexOf(a.phase) - LAB_PHASE_SEQUENCE.indexOf(b.phase) ||
      (a.order || 0) - (b.order || 0),
  );

/** Every step, keyed by id, so `depends_on` and `skip_if_past` can be shown as step keys. */
const stepsById = (scenario: LabScenario): Record<number, LabScenarioStep> =>
  Object.fromEntries(sortedSteps(scenario).map((step) => [step.id, step]));

export type LabScenarioStepsPanelProps = LabScenarioPanelProps & {
  /** Restrict to these phases. Omit for every phase, in phase order. */
  phases?: LabPhase[];
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
};

/**
 * The steps, optionally narrowed to some phases.
 *
 * A new step created from a narrowed panel is seeded into the first phase the panel shows -
 * adding a check from the validation tab and having it default to bootstrap is a trap, because
 * a bootstrap step that asserts something is a step that fails the lab before it starts.
 */
export const LabScenarioStepsPanel: React.FC<LabScenarioStepsPanelProps> = ({
  scenario,
  canEdit = false,
  phases,
  title = 'Steps',
  subtitle = 'In phase order. A gated step waits for a trigger; a catch-up step is skipped for anyone past its anchor.',
  emptyMessage = 'No steps yet. A run against this scenario would open and finish with nothing done.',
}) => {
  const [editor, setEditor] = useState<{ open: boolean; step: LabScenarioStep | null }>({
    open: false,
    step: null,
  });
  const [injecting, setInjecting] = useState<LabScenarioStep | null>(null);
  const deleteStep = useDeleteLabRow(LAB_TABLES.step);
  const { remove, removeErrorNode } = useRowRemoval();

  const byId = stepsById(scenario);
  const steps = sortedSteps(scenario).filter((step) => !phases || phases.includes(step.phase));

  return (
    <>
      <LabCard
        title={`${title} (${steps.length})`}
        subtitle={subtitle}
        actions={
          canEdit ? (
            <StbButton
              role="add"
              size="small"
              onClick={async () => setEditor({ open: true, step: null })}
            >
              New step
            </StbButton>
          ) : undefined
        }
      >
        {removeErrorNode}

        {steps.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Step</th>
                  <th className="px-3 py-2 font-semibold">Phase</th>
                  <th className="px-3 py-2 font-semibold">Action</th>
                  <th className="px-3 py-2 font-semibold">Insertion</th>
                  <th className="px-3 py-2 font-semibold">Reach</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {steps.map((step) => {
                  const blockers = labStepInjectionBlockers(step);
                  const dependsOn = (step.depends_on || [])
                    .map((depId) => byId[depId]?.key)
                    .filter(Boolean);

                  return (
                    <tr key={step.id} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{step.key}</div>
                        <div className="text-xs text-gray-500">{step.name}</div>

                        <div className="mt-1 flex flex-wrap gap-1">
                          {!step.enabled && <LabStatusTag label="Disabled" sentiment="warning" />}
                          {step.gated && <LabStatusTag label="Gated" sentiment="warning" />}
                          {step.optional && <LabStatusTag label="Optional" />}
                          {step.recurring_seconds && (
                            <LabStatusTag
                              label={`Repeats every ${labDuration(step.recurring_seconds)}`}
                            />
                          )}
                          {step.sensitive_output && (
                            <LabStatusTag label="Output withheld" sentiment="info" />
                          )}
                        </div>

                        {dependsOn.length > 0 && (
                          <div className="mt-1 text-xs text-gray-500">
                            {`Depends on ${dependsOn.join(', ')}`}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {labPhaseLabel(step.phase)}
                        <div className="text-xs text-gray-500">{`order ${step.order}`}</div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {step.action_type}
                        <div className="text-xs text-gray-500">
                          {labDuration(step.timeout_seconds)}
                          {step.retry_limit ? `, ${step.retry_limit} retries` : ''}
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {labInsertModeLabel(step.insert_mode)}
                        {step.insert_mode === 'catch_up' && (
                          <div className="text-xs text-gray-500">
                            {step.skip_if_past
                              ? `after ${byId[step.skip_if_past]?.key || step.skip_if_past}`
                              : 'no anchor set'}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        {step.blast_radius ? (
                          <LabStatusTag
                            label={step.blast_radius}
                            sentiment={step.blast_radius === 'shared' ? 'danger' : 'info'}
                          />
                        ) : (
                          <LabStatusTag label="Not stated" sentiment="warning" />
                        )}
                        <div className="text-xs text-gray-500">
                          {step.disruptive === null || step.disruptive === undefined
                            ? 'disruptive not stated'
                            : step.disruptive
                              ? 'disruptive'
                              : 'not disruptive'}
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {/* Only offered where the backend would accept it, with the reasons
                              shown in the modal when it would not. */}
                          {canEdit && step.insert_mode !== 'at_start' && (
                            <StbButton
                              role="request"
                              size="xsmall"
                              disabled={blockers.length > 0}
                              onClick={async () => setInjecting(step)}
                            >
                              Push to pods
                            </StbButton>
                          )}
                          {canEdit && (
                            <>
                              <StbButton
                                role="update"
                                size="xsmall"
                                onClick={async () => setEditor({ open: true, step })}
                              >
                                Edit
                              </StbButton>
                              <StbButton
                                role="delete"
                                size="xsmall"
                                loading={deleteStep.isPending}
                                onClick={async () => remove(deleteStep, step.id, 'step')}
                              >
                                Delete
                              </StbButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="m-0 text-sm text-gray-600">{emptyMessage}</p>
        )}
      </LabCard>

      <LabStepEditorModal
        open={editor.open}
        scenario={scenario}
        step={editor.step}
        defaultPhase={phases?.length ? phases[0] : undefined}
        onClose={() => setEditor({ open: false, step: null })}
      />

      <LabInjectionModal
        open={!!injecting}
        scenario={scenario}
        step={injecting}
        onClose={() => setInjecting(null)}
      />
    </>
  );
};

/** What the run is scored on. Progress and score are separate questions. */
export const LabScenarioCriteriaPanel: React.FC<LabScenarioPanelProps> = ({
  scenario,
  canEdit = false,
}) => {
  const [editor, setEditor] = useState<{ open: boolean; criterion: LabSuccessCriterion | null }>({
    open: false,
    criterion: null,
  });
  const deleteCriterion = useDeleteLabRow(LAB_TABLES.criterion);
  const { remove, removeErrorNode } = useRowRemoval();

  const byId = stepsById(scenario);
  const criteria = [...(scenario.success_criteria || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );

  return (
    <>
      <LabCard
        title={`Success criteria (${criteria.length})`}
        subtitle="What counts as a pass. A run can finish every step and still not score."
        actions={
          canEdit ? (
            <StbButton
              role="add"
              size="small"
              onClick={async () => setEditor({ open: true, criterion: null })}
            >
              New criterion
            </StbButton>
          ) : undefined
        }
      >
        {removeErrorNode}

        {criteria.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Criterion</th>
                  <th className="px-3 py-2 font-semibold">Evaluator</th>
                  <th className="px-3 py-2 font-semibold">Weight</th>
                  <th className="px-3 py-2 font-semibold">Depends on</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {criteria.map((criterion) => (
                  <tr key={criterion.id} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium">{criterion.key}</div>
                      <div className="text-xs text-gray-500">{criterion.name}</div>
                      {criterion.required && <LabStatusTag label="Required" sentiment="info" />}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      {labEvaluatorLabel(criterion.evaluator)}
                      {criterion.hold_seconds ? (
                        <div className="text-xs text-gray-500">
                          {`holds for ${labDuration(criterion.hold_seconds)}`}
                        </div>
                      ) : null}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">{criterion.weight}</td>

                    <td className="px-3 py-2">
                      <div className="text-xs text-gray-600">
                        {(criterion.steps || [])
                          .map((stepId) => byId[stepId]?.key)
                          .filter(Boolean)
                          .join(', ') || '-'}
                      </div>
                    </td>

                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {canEdit && (
                          <>
                            <StbButton
                              role="update"
                              size="xsmall"
                              onClick={async () => setEditor({ open: true, criterion })}
                            >
                              Edit
                            </StbButton>
                            <StbButton
                              role="delete"
                              size="xsmall"
                              loading={deleteCriterion.isPending}
                              onClick={async () => remove(deleteCriterion, criterion.id, 'criterion')}
                            >
                              Delete
                            </StbButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="m-0 text-sm text-gray-600">
            No criteria yet, so a run has nothing to be scored against.
          </p>
        )}
      </LabCard>

      <LabCriterionEditorModal
        open={editor.open}
        scenario={scenario}
        criterion={editor.criterion}
        onClose={() => setEditor({ open: false, criterion: null })}
      />
    </>
  );
};

/**
 * The devices this scenario is attached to.
 *
 * Attached, not owned. Every dCloud pod is built from the same template, so a box is authored once
 * into the library and the sections that use it hold a link - which is why this panel offers
 * "Attach existing" beside "New device", and why the row action is Detach rather than Delete.
 * Deleting here would take the box out from under every other section that has it; the library view
 * is where that decision belongs, with the full blast radius on screen.
 *
 * Addresses are pod-internal and overridable per publication. Credentials are referenced, never
 * stored.
 */
export const LabScenarioDevicesPanel: React.FC<LabScenarioPanelProps> = ({
  scenario,
  canEdit = false,
}) => {
  const [editor, setEditor] = useState<{ open: boolean; device: LabDevice | null }>({
    open: false,
    device: null,
  });
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const attach = useAttachLabDevices();
  const detach = useDetachLabDevice();
  const library = useLabDevices({}, { enabled: attaching });

  const devices = [...(scenario.devices || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  /** The link row for this scenario, which is what a detach deletes. */
  const linkFor = (device: LabDevice) =>
    (device.scenario_links || []).find((link) => link.scenario === scenario.id) || null;

  const detachDevice = async (device: LabDevice) => {
    const link = linkFor(device);

    if (!link) return;

    const elsewhere = (device.scenario_links || []).length - 1;

    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        `Remove ${device.display_name || device.device_key} from ${scenario.name}? The device stays in the library${
          elsewhere > 0
            ? ` and in the ${elsewhere} other section${elsewhere === 1 ? '' : 's'} that use it`
            : ''
        }. Anything applied to it in this section has to be detached first.`,
      )
    ) {
      return;
    }

    setAttachError(null);

    try {
      await detach.mutateAsync(link.id);
    } catch (exc) {
      setAttachError(labErrorMessage(exc, 'That device could not be detached.'));
    }
  };

  return (
    <>
      <LabCard
        title={`Devices (${devices.length})`}
        subtitle="Attached from the library - every pod is the same template, so the same box serves every section that needs it. Addresses are pod-internal and overridable per publication; credentials are referenced, never stored."
        actions={
          canEdit ? (
            <div className="flex flex-wrap gap-2">
              <StbButton
                role="add"
                size="small"
                onClick={async () => setEditor({ open: true, device: null })}
              >
                New device
              </StbButton>
              <StbButton role="default" size="small" onClick={async () => setAttaching(true)}>
                Attach existing
              </StbButton>
            </div>
          ) : undefined
        }
      >
        {/* No `useRowRemoval` here: nothing on this panel deletes a row. A detach is a link that
            goes, and its failure is reported in place. */}
        {attachError && <p className="m-0 mb-2 text-sm text-amber-800">{attachError}</p>}

        {devices.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Device</th>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Transport</th>
                  <th className="px-3 py-2 font-semibold">Address</th>
                  <th className="px-3 py-2 font-semibold">Credential</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {devices.map((device) => (
                  <tr key={device.id} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium">{device.device_key}</div>
                      <div className="text-xs text-gray-500">
                        {[device.display_name, device.platform, device.role]
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {device.required && <LabStatusTag label="Required" />}
                        {device.monitored && <LabStatusTag label="Monitored" />}
                        {!device.verify_tls && (
                          <LabStatusTag label="TLS not verified" sentiment="warning" />
                        )}
                        {/* How far an edit here reaches. Worth a tag rather than a footnote: the
                            Edit button below opens the library row, not a copy of it. */}
                        {(device.scenario_links || []).length > 1 && (
                          <LabStatusTag
                            label={`In ${(device.scenario_links || []).length} sections`}
                          />
                        )}
                      </div>
                    </td>

                    {/* What items are checked against when one is attached here. Untyped is a
                        real state and a permissive one, so it is named rather than left blank. */}
                    <td className="px-3 py-2">
                      {device.device_type ? (
                        <>
                          <div>{device.device_type_name || device.device_type_key}</div>
                          {/* A family type names no product, so the device does. Unanswered is
                              flagged: nothing knows which API to speak to the box. */}
                          {device.device_type_requires_product ? (
                            <div
                              className={`text-xs ${
                                device.product_uid ? 'text-gray-500' : 'text-amber-800'
                              }`}
                            >
                              {device.product_name || 'no product chosen'}
                            </div>
                          ) : (
                            device.device_type_product_name && (
                              <div className="text-xs text-gray-500">
                                {device.device_type_product_name}
                              </div>
                            )
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-amber-800">No type</span>
                      )}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      {labTransportLabel(device.transport)}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                      {device.address_template || '-'}
                      {device.port ? `:${device.port}` : ''}
                    </td>

                    <td className="px-3 py-2 font-mono text-xs">{device.credential_ref || '-'}</td>

                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {canEdit && (
                          <>
                            <StbButton
                              role="update"
                              size="xsmall"
                              onClick={async () => setEditor({ open: true, device })}
                            >
                              Edit
                            </StbButton>
                            {/* Detach, not delete. The row belongs to the library, and every other
                                section that attached it keeps it. */}
                            <StbButton
                              role="delete"
                              size="xsmall"
                              loading={detach.isPending}
                              onClick={async () => detachDevice(device)}
                            >
                              Detach
                            </StbButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="m-0 text-sm text-gray-600">
            No devices attached, so this section&apos;s items have nothing to run against. Attach the
            box from the library if the pod already has one.
          </p>
        )}
      </LabCard>

      <LabDeviceEditorModal
        open={editor.open}
        scenario={scenario}
        device={editor.device}
        onClose={() => setEditor({ open: false, device: null })}
      />

      {attaching && (
        <LabAttachPicker<LabDevice>
          open
          title={`Attach devices to ${scenario.name}`}
          subtitle="One library, one box per machine in the pod. Attaching does not copy it: a later fix to the address or the credential reference reaches every section that has it, which is the point of a shared template."
          candidates={library.data || []}
          loading={library.isLoading}
          error={library.error}
          nameOf={(device) => (
            <span>
              <span className="font-medium">{device.display_name || device.device_key}</span>
              <span className="ml-2 font-mono text-xs text-gray-500">{device.device_key}</span>
            </span>
          )}
          search={(device, term) =>
            `${device.display_name} ${device.device_key} ${device.role} ${
              device.address_template
            } ${device.device_type_name || ''} ${device.product_name || ''}`
              .toLowerCase()
              .includes(term)
          }
          summaryOf={(device) =>
            [
              device.device_type_name || 'no type',
              device.product_name || null,
              device.address_template || 'no address',
              labTransportLabel(device.transport),
            ]
              .filter(Boolean)
              .join(' • ')
          }
          usageOf={(device) =>
            (device.scenario_links || []).length
              ? `Already in ${(device.scenario_links || []).length} section${
                  (device.scenario_links || []).length === 1 ? '' : 's'
                } - an edit to it reaches all of them`
              : 'Not in any section yet'
          }
          attachedIds={devices.map((device) => device.id)}
          attachedReason={() => 'Already attached to this section.'}
          submitLabel="Attach"
          emptyMessage="The device library is empty. Author a box with New device."
          onClose={() => setAttaching(false)}
          onAttach={async (picked) => {
            await attach.mutateAsync({
              scenarioId: scenario.id,
              deviceIds: picked.map((entry) => entry.row.id),
            });
          }}
        />
      )}
    </>
  );
};

/**
 * Triggers.
 *
 * Fetched rather than read off the scenario: they are a separate table with no nesting on the
 * scenario serializer, so composing them from `scenario.triggers` would quietly show none.
 */
export const LabScenarioTriggersPanel: React.FC<LabScenarioPanelProps> = ({
  scenario,
  canEdit = false,
}) => {
  const [editor, setEditor] = useState<{ open: boolean; trigger: LabScenarioTrigger | null }>({
    open: false,
    trigger: null,
  });
  const deleteTrigger = useDeleteLabRow(LAB_TABLES.trigger);
  const { remove, removeErrorNode } = useRowRemoval();

  const { data: triggers, isLoading, error } = useLabScenarioTriggers(scenario.id);
  const byId = stepsById(scenario);

  return (
    <>
      <LabCard
        title={`Triggers (${(triggers || []).length})`}
        subtitle="The only thing that releases a gated step. Fetched separately - the scenario does not carry them."
        actions={
          canEdit ? (
            <StbButton
              role="add"
              size="small"
              onClick={async () => setEditor({ open: true, trigger: null })}
            >
              New trigger
            </StbButton>
          ) : undefined
        }
      >
        {removeErrorNode}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!(triggers || []).length}
          emptyMessage="No triggers. Any gated step in this scenario stays blocked for the whole lab."
          loadingLabel="Loading triggers..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Trigger</th>
                  <th className="px-3 py-2 font-semibold">Fires</th>
                  <th className="px-3 py-2 font-semibold">Does</th>
                  <th className="px-3 py-2 font-semibold">Targets</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {(triggers || []).map((trigger) => (
                  <tr key={trigger.id} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium">{trigger.key}</div>
                      <div className="text-xs text-gray-500">{trigger.name}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {!trigger.enabled && <LabStatusTag label="Disabled" sentiment="warning" />}
                        {trigger.fire_once && <LabStatusTag label="Once" />}
                      </div>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      {labTriggerTypeLabel(trigger.trigger_type)}
                      {trigger.hold_seconds ? (
                        <div className="text-xs text-gray-500">
                          {`after holding ${labDuration(trigger.hold_seconds)}`}
                        </div>
                      ) : null}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      {labTriggerActionLabel(trigger.action)}
                      {trigger.event_name && (
                        <div className="text-xs text-gray-500">{trigger.event_name}</div>
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs text-gray-600">
                      {(
                        trigger.target_step_keys ||
                        (trigger.target_steps || []).map((stepId) => byId[stepId]?.key)
                      )
                        .filter(Boolean)
                        .join(', ') || '-'}
                    </td>

                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {canEdit && (
                          <>
                            <StbButton
                              role="update"
                              size="xsmall"
                              onClick={async () => setEditor({ open: true, trigger })}
                            >
                              Edit
                            </StbButton>
                            <StbButton
                              role="delete"
                              size="xsmall"
                              loading={deleteTrigger.isPending}
                              onClick={async () => remove(deleteTrigger, trigger.id, 'trigger')}
                            >
                              Delete
                            </StbButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabTriggerEditorModal
        open={editor.open}
        scenario={scenario}
        trigger={editor.trigger}
        onClose={() => setEditor({ open: false, trigger: null })}
      />
    </>
  );
};
